/**
 * Product Innovation Lab - Core Types
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business プロダクトコア型定義。共通エンティティ・ページネーション・エラー
 * @graph-connects none
 */
/**
 * `PortalRole` の全値を含む読み取り専用配列。
 *
 * 各 app の zod スキーマなどから core-types の SSOT を参照するために提供する。
 * core-types に zod 依存を持ち込まないため、配列のみを export する。
 * @graph-connects none
 */
export const PORTAL_ROLES = [
    'admin',
    'group_manager',
    'team_leader',
    'member',
    'architect',
    'dle',
    'tech_lead',
    'pmo',
    'external',
];
/** @graph-connects none */
const PORTAL_ROLE_SET = new Set(PORTAL_ROLES);
/**
 * 任意の値が組織ポジションとしての `PortalRole` かを判定する。
 * @graph-connects none
 */
export function isPortalRole(value) {
    return typeof value === 'string' && PORTAL_ROLE_SET.has(value);
}
/**
 * エンジニア組織（Product Innovation Div.）の `division` コード。
 *
 * workspace-pipeline が Google Workspace の orgUnitPath 先頭セグメントから同期する
 * 組織所属の SSOT。Product Innovation Lab の内部/外部判定はこの値を基準にする。
 * @graph-connects none
 */
export const ENGINEERING_DIVISION = 'pi_div';
/**
 * Interface Design チーム（IDT / デザイナー）の `teamName` コード。
 *
 * workspace-pipeline が Google Workspace の組織から同期する Firestore `users.teamName` の値。
 * IDT はグループが `pi_pdg`（PDG）配下だが team は `pi_idt` で、同じ PDG のエンジニア
 * （`pi-tet` / `pi_sret`）とは別に扱う。team 名は他グループと重複しないため、team 単独で判別できる。
 *
 * **リテラルはここだけに置く。** デザイナーにだけ開く操作は入口（画面・MCP・API）ごとに判定が
 * 生まれるので、値を写すと片方だけ直って許可範囲がサイレントにずれる。判定は
 * `isInterfaceDesignTeam` を通し、入口はこの値を持たない。
 * @graph-connects none
 */
export const INTERFACE_DESIGN_TEAM = 'pi_idt';
/**
 * PMO チーム（PMOT）の `teamName` コード。
 *
 * `workspace.unified_members.team_name` / Firestore `users.teamName` で PMO チームを表す値。
 * PMO 看板の権限判定・軽タスク セルフ・アサイン（PMO は事業制限なし）・案件アサインの許可判定で使う。
 *
 * **リテラルはここだけに置く。** `@cortex/domain-product-innovation-lab-combat-power-scope` の
 * `PMO_TEAM_NAME` はこの値の re-export で、`INTERFACE_DESIGN_TEAM` / `IDT_TEAMS` と同じ関係
 * （組織コードの正本は core-types、集計用の集合はドメイン側が包む）。
 * @graph-connects none
 */
export const PMO_TEAM = 'pi_pmot';
/**
 * PDG グループ配下の `teamName` コード一覧（権限判定用）。
 *
 * `workspace.unified_members` の実値は `group_name='pi_pdg'` に対し team が
 * `pi_pmot`（PMOT） / `pi_idt`（IDT） / `pi-tet`（TET） / `pi_sret`（SRE）。SRE は
 * `group_name` が未同期（NULL）の行があるため group では引けず、team で判定する。
 *
 * **`@cortex/domain-product-innovation-lab-combat-power-scope` の `PDG_TEAMS` とは口径が違う。**
 * あちらは戦闘力集計の対象集合で、IDT / PMOT を意図的に除く（`{pi-tet, pi_sret}`）。こちらは
 * 「PDG グループに所属しているか」で、除外しない。同じ名前の集合に見えるが混ぜてはいけない。
 * @graph-connects none
 */
export const PDG_TEAMS_FOR_PERMISSION = [
    PMO_TEAM,
    INTERFACE_DESIGN_TEAM,
    'pi-tet',
    'pi_sret',
];
/**
 * Interface Design チーム（IDT / デザイナー）所属かを `teamName` だけで判定する。
 *
 * `division` を見ないのは、IDT が `pi_div` 配下にいるとは限らない（業務委託・兼務で
 * `division` が空のことがある）一方、`teamName` は他グループと重複しないため。
 * 未同期・未設定（null / undefined / 空文字）は false に倒す（所属不明を許可に倒さない）。
 * @graph-connects none
 */
export function isInterfaceDesignTeam(teamName) {
    return (teamName ?? '').trim().toLowerCase() === INTERFACE_DESIGN_TEAM;
}
/**
 * Product Innovation Lab の「内部メンバー（full access）」判定。
 *
 * 内部とみなす条件は次のいずれか:
 * - `division === 'pi_div'`（workspace-pipeline 同期の組織 SSOT に基づく所属判定）
 * - `member` / `external` 以外の明示付与ロールを持つ（アカウント管理で個別に
 *   権限を割り当てられたユーザー。`division` が空の役員等を救済する）
 *
 * `organizationId`(PDG/ACE) は workspace-pipeline が同期せず値が不揃いのため判定軸に
 * しない。`member` は初回ログイン時に全社員へ自動付与されるフォールバックロールなので、
 * これ単体では内部判定の根拠にしない（所属 `division` 側で判定する）。
 *
 * 複数ロール保持時は「`member`/`external` 以外のロールを1つでも持つか」の existential
 * チェック（優先順位ではない）。
 * @graph-connects none
 */
export function isInternalPortalMember(division, portalRoles) {
    if (division === ENGINEERING_DIVISION)
        return true;
    return (portalRoles ?? []).some((role) => role !== 'member' && role !== 'external');
}
/**
 * Product Innovation Lab の「PI Div 所属者・admin」判定（PI Div 専用機能のフルアクセス対象）。
 *
 * 条件は次のいずれか:
 * - `division === 'pi_div'`（PI Div 所属の社員）
 * - `admin` ロールを保持する（横断 admin。Div 不問）
 *
 * KPI / アカウント管理 / メンバー / GitHub Activity 等の PI Div 専用機能の開放対象を表す。
 * 横断 PMO（ac_div/mall_div/log_div × pmo）は false（`/projects` と `/my-page` のみ）。
 * フロント（`usePageAccess` / `canAccessPage`）とバックエンド（`requirePiDivOrAdmin`）が
 * 同一述語を参照するための SSOT。
 * @graph-connects none
 */
export function isPiDivOrAdmin(division, portalRoles) {
    return division === ENGINEERING_DIVISION || (portalRoles?.includes('admin') ?? false);
}
/**
 * `CrossDivPmoDivision` の全値を含む読み取り専用配列。
 * @graph-connects none
 */
export const CROSS_DIV_PMO_DIVISIONS = [
    'ac_div',
    'mall_div',
    'log_div',
];
/** @graph-connects none */
const CROSS_DIV_PMO_DIVISION_SET = new Set(CROSS_DIV_PMO_DIVISIONS);
/** @graph-connects none */
export const CROSS_DIV_PMO_SCOPE = {
    ac_div: {
        divisionLabel: 'aC Div',
        businesses: ['エアクロ', 'Spot Rental', 'エアクロメンズ'],
        badgeColor: '#DC4040',
    },
    mall_div: {
        divisionLabel: 'Mall Div',
        businesses: ['エアクロモール'],
        badgeColor: '#50BA52',
    },
    log_div: {
        // 注意: BQ 側 `pb_sheet_sync.division` の値は 'Log Div'（短縮形）。UI 側の divLabel は
        // 'Logi Div'（プロダクト固有の社内呼称）でフロント表示用。BQ の divisionLabel は WHERE 句で
        // 等価マッチに使うので絶対に変更しないこと。両者の対応は
        // `apps/web/product-innovation-lab/src/lib/cross-div-pmo-ui.ts` の `log_div.divLabel` 参照。
        divisionLabel: 'Log Div',
        businesses: ['AC PORT'],
        extraGroup: 'SCMG',
        badgeColor: '#56C3DA',
    },
};
/**
 * `division` コードが横断 PMO 対象 Div か判定する型ガード。
 * @graph-connects none
 */
export function isCrossDivPmoDivision(division) {
    return typeof division === 'string' && CROSS_DIV_PMO_DIVISION_SET.has(division);
}
/**
 * 横断 PMO ユーザー（pi_div 以外 × `pmo` ロールを保持）の `/projects` スコープを返す。
 * pi_div の PMO / admin / 非 PMO の非 pi_div 社員は null（横断スコープは適用しない）。
 *
 * `admin` ロールは横断対象外で常に全社閲覧（呼び出し側で null として扱う）。
 * 複数ロール保持時は「`pmo` を1つでも持つか」の existential チェック。
 * @graph-connects none
 */
export function resolveCrossDivPmoScope(division, portalRoles) {
    if (!(portalRoles?.includes('pmo') ?? false))
        return null;
    if (!isCrossDivPmoDivision(division))
        return null;
    return division;
}
/**
 * workspace.users.team_name（例: `ac_nbt` / `m_bdt` / `log_lpt`）を、Backlog 系テーブル
 * （`project_detail_table.responsible_team` / `pb_sheet_sync.responsible_team`）で使われる
 * 大文字ハイフン区切り表記（例: `NBT` / `M-BDT` / `LPT`）に変換する。
 *
 * 規約:
 *   - `ac_<x>`  → `<X>`（プレフィクスを取り除いて大文字化）
 *   - `log_<x>` → `<X>`（同上）
 *   - `m_<x>`   → `M-<X>`（Mall Div のみ pb_sheet_sync 側が `M-` プレフィクス）
 *
 * 期待する `division` 以外のプレフィクスを持つチームコード（旧称・別 Div の出向など）は
 * 変換できないため null を返す。
 * @graph-connects none
 */
export function mapWorkspaceTeamToResponsibleTeam(division, workspaceTeamName) {
    if (!workspaceTeamName)
        return null;
    const lower = workspaceTeamName.toLowerCase();
    const prefix = division === 'mall_div' ? 'm_' : division === 'log_div' ? 'log_' : 'ac_';
    if (!lower.startsWith(prefix))
        return null;
    const suffix = lower.substring(prefix.length).toUpperCase();
    if (suffix.length === 0)
        return null;
    return division === 'mall_div' ? `M-${suffix}` : suffix;
}
/**
 * `/projects` ページのスコープを user の (division, portalRoles, teamName) から決定する。
 *
 * - 非 pi_div × `pmo` を保持     → Div 全体スコープ（lockedTeam=null）
 * - 非 pi_div × `team_leader` を保持（`pmo` 非保持） → 所属チームに絞ったスコープ
 * - pi_div / admin / その他       → null（全社表示）
 *
 * 複数ロール保持時は「各保持ロールが許可するスコープの union」で考える。`pmo` の許可範囲
 * （Div 全体）は `team_leader` の許可範囲（自チームのみ）を包含するため、両方を保持していても
 * union は Div 全体に収束する。これは「pmo が team_leader に優先する」というランキングでは
 * なく、pmo 側のスコープが team_leader 側のスコープを包含する結果 union がそう収束するだけ。
 * 実装上は pmo を先に判定しているだけで、優先順位を意図したものではない。
 *
 * `team_leader` でも workspace.users.team_name が Div プレフィクスに一致しない場合
 * （未同期・例外 OU 配置）は null を返し、テーブル全件を返さない側に倒す（呼び出し元で
 * 403 もしくは「該当案件なし」として扱う）。
 * @graph-connects none
 */
export function resolveProjectScopedView(division, portalRoles, teamName) {
    if (!isCrossDivPmoDivision(division))
        return null;
    const roles = portalRoles ?? [];
    if (roles.includes('pmo')) {
        return { division, lockedTeam: null };
    }
    if (roles.includes('team_leader')) {
        const lockedTeam = mapWorkspaceTeamToResponsibleTeam(division, teamName);
        if (!lockedTeam)
            return null;
        return { division, lockedTeam };
    }
    return null;
}
/**
 * `/projects` のアクセスモードを user の (division, portalRoles, teamName) から決定する。
 *
 * `resolveProjectScopedView` を null/scope の 2 値で返すと、呼び出し側で
 * 「null = 全社閲覧」と扱われて非 pi_div × 非 admin のロールが意図せず全社案件を
 * 見られてしまう（例: `non-pi_div × team_leader` で teamName が Div プレフィクスに
 * 一致しないケース、`administration_office × pmo` 等）。
 *
 * この関数は 3 値を返し、呼び出し側で「空配列を返す」分岐を明示できるようにする。
 *
 * 規約:
 * - `pi_div` / `admin` を保持 → `unscoped`（全社案件）
 * - 非 pi_div × 非 admin で scope 取得成功 → `scoped`
 * - 非 pi_div × 非 admin で scope 取得失敗 → `empty`（全社閲覧へフォールスルーしない）
 *
 * @graph-connects none
 */
export function resolveProjectAccessMode(division, portalRoles, teamName) {
    if (isPiDivOrAdmin(division, portalRoles)) {
        return { kind: 'unscoped' };
    }
    const scope = resolveProjectScopedView(division, portalRoles, teamName);
    if (scope)
        return { kind: 'scoped', scope };
    return { kind: 'empty' };
}
/**
 * Firestore `users.productPortalRoles`（配列, SSOT）優先で読み、未 backfill のドキュメント
 * は旧 `users.productPortalRole`（scalar）にフォールバックする共通ヘルパー。
 * 複数ロール保持時の代表値導出は `derivePrimaryPortalRole` と同じ優先順位（admin優先、
 * 以降 `PORTAL_ROLES` 宣言順）に従う。
 * @graph-connects none
 */
export function resolvePortalRoleFromFirestore(productPortalRoles, legacyProductPortalRole) {
    const roles = Array.isArray(productPortalRoles) ? productPortalRoles.filter(isPortalRole) : [];
    const primary = derivePrimaryPortalRole(roles);
    if (primary)
        return primary;
    return isPortalRole(legacyProductPortalRole) ? legacyProductPortalRole : undefined;
}
/**
 * Firestore `users.productPortalRoles`（配列, SSOT）を保持ロールの配列として読む。
 * 未 backfill のドキュメントは旧 `users.productPortalRole`（scalar）を 1 要素の配列に倒す
 * （`resolvePortalRoleFromFirestore` と同じ倒し方）。`checkPermission` は保持ロール全部で
 * 判定するので、代表値 1 つに潰さずに渡すための口である。
 * @graph-connects none
 */
export function resolvePortalRolesFromFirestore(productPortalRoles, legacyProductPortalRole) {
    const roles = Array.isArray(productPortalRoles) ? productPortalRoles.filter(isPortalRole) : [];
    if (roles.length > 0)
        return roles;
    return isPortalRole(legacyProductPortalRole) ? [legacyProductPortalRole] : [];
}
/**
 * ポータル権限定義（中央集権管理）
 *
 * 各業務権限に対してアクセス可能なロールを定義する。
 * アプリゲート（特定アプリへのアクセス可否）はこの定義に追加せず、
 * 各アプリ側の middleware と Firestore `app_access` collection で扱う。
 *
 * @graph-connects none
 */
export const PORTAL_PERMISSIONS = {
    /** アカウント管理（pmo は member ロールのみ追加/編集/削除可能。詳細は apps/api/product 側で enforce） */
    MANAGE_ACCOUNTS: ['admin', 'group_manager', 'pmo'],
    /** QA評価閲覧（team_leaderはQAチーム所属のみ） */
    VIEW_QA: {
        roles: ['admin', 'group_manager'],
        teamConditions: {
            team_leader: ['QA team'],
        },
    },
    /** メンバー管理閲覧 */
    VIEW_MEMBERS: ['admin', 'group_manager', 'team_leader', 'pmo'],
    /** KPIチャットアシスタント */
    VIEW_KPI_CHAT: ['admin', 'group_manager', 'team_leader'],
    /** プロジェクト管理閲覧 */
    VIEW_PROJECTS: ['admin', 'group_manager', 'team_leader', 'dle', 'pmo'],
    /**
     * PL/主担当看板閲覧（全 PortalRole + external）。
     *
     * Product Innovation Lab を全社員に開放するための入口ページ。各社員が自分の担当案件
     * （PL = `project_detail_table.assignee`、主担当 = `pb_sheet_sync.main_worker`）を
     * `users.nickName` でマッチングして閲覧する。portalRole 未設定者も `auth/me` 側で
     * 'external' にフォールバックされるため、ここでは全ロールを許可する。
     */
    VIEW_MY_PROJECTS_BOARD: [
        'admin',
        'group_manager',
        'team_leader',
        'member',
        'architect',
        'dle',
        'tech_lead',
        'pmo',
        'external',
    ],
    /**
     * 案件起票（`/my-page` → ＋案件を起票する）。所属 group / division を問わず全メンバーに
     * 開放するため、全 PortalRole + `external`（portalRole 未設定者の兜底）を許可する。
     *
     * `VIEW_MY_PROJECTS_BOARD` と同じ「全社員開放系」の入口機能だが、こちらは Backlog チケット
     * ツリー・**公開** Slack チャンネル・共有シート追記・GAS ファイルコピーという不可逆な書き込みを
     * 伴う点が異なる。この範囲拡大に伴う競合・濫用面の受容判断は
     * `docs/web/product-innovation-lab.md`「案件起票 > アクセス制御 / 設計判断」に記録する。
     */
    CREATE_PROJECT_INTAKE: [
        'admin',
        'group_manager',
        'team_leader',
        'member',
        'architect',
        'dle',
        'tech_lead',
        'pmo',
        'external',
    ],
    /**
     * デザイナー貢献度アンケートへの回答（MyPage デザイン view の「アンケート」列）。
     *
     * 回答者は案件の PL / 主担当であり、事業部所属で portalRole が 'external' に
     * フォールバックするメンバーを含む。そのため `CREATE_PROJECT_INTAKE` と同様に
     * 全 PortalRole + `external` を許可する「全社員開放系」の permission とする。
     *
     * ただしこれは**入口の開放**であって、誰がどの案件に回答できるかの制御ではない。
     * 「acting user がその親チケットの PL または主担当であること」は API 側で必ず別途
     * 検証すること（permission だけでは任意の external が任意のデザイナーを評価できる）。
     */
    SUBMIT_DESIGN_EVALUATION: [
        'admin',
        'group_manager',
        'team_leader',
        'member',
        'architect',
        'dle',
        'tech_lead',
        'pmo',
        'external',
    ],
    /**
     * DLE 評価アンケートへの回答（MyPage 開発 view の「アンケート」列）。
     *
     * `SUBMIT_DESIGN_EVALUATION` と同じ「全社員開放系」。回答者は案件の PL / 主担当であり、
     * 事業部所属で portalRole が 'external' にフォールバックするメンバーを含む。
     *
     * ただしこれは**入口の開放**であって、誰がどの案件に回答できるかの制御ではない。
     * 「acting user がその母票の PL または主担当であること」は API 側で必ず別途検証すること
     * （permission だけでは任意の external が任意の DLE を評価できる）。
     *
     * デザイナー貢献度アンケートと permission を共有しないのは、対象者（デザイナー / DLE）も
     * 評価の用途も別で、片方だけ閉じたいときに分けられないと困るため。
     */
    SUBMIT_DLE_EVALUATION: [
        'admin',
        'group_manager',
        'team_leader',
        'member',
        'architect',
        'dle',
        'tech_lead',
        'pmo',
        'external',
    ],
    /** PMO看板閲覧・編集（PMOチーム = pi_pmot 所属者 or 管理者のみ） */
    VIEW_PMO_BOARD: {
        roles: ['admin', 'group_manager'],
        teamConditions: {
            team_leader: ['pi_pmot'],
            member: ['pi_pmot'],
            pmo: ['pi_pmot'],
            dle: ['pi_pmot'],
            architect: ['pi_pmot'],
            tech_lead: ['pi_pmot'],
        },
    },
    /**
     * PMO看板の「実績集計」タブだけを開く権限。
     *
     * **`VIEW_PMO_BOARD` の真の上位集合**（PMOT 所属の全ロール + admin/group_manager に、
     * PDG 全チームの `team_leader` を足したもの）。実績集計の API / 画面はこちら 1 本だけを
     * 見ればよく、`VIEW_PMO_BOARD` との OR を書く必要はない。
     *
     * 分けている理由: PMO看板の他タブ（期限超過 / 期限直近 / 漏れ管理）は PMO が案件を追い回す
     * ための作業面で、期限の書き戻しまでできる。実績集計は読み取りの集計レポートで、自チームが
     * どれだけ出したかを PDG の各リーダーが見る用途。同じページに同居しているだけで、開放範囲を
     * 揃える理由がない。タブの出し分けは画面側（`projects.pmo-board.tsx`）が行う。
     */
    VIEW_PMO_PERFORMANCE_AGG: {
        roles: ['admin', 'group_manager'],
        teamConditions: {
            // PMOT 所属はロールを問わず許可する（VIEW_PMO_BOARD と同じ範囲）。
            member: [PMO_TEAM],
            pmo: [PMO_TEAM],
            dle: [PMO_TEAM],
            architect: [PMO_TEAM],
            tech_lead: [PMO_TEAM],
            // team_leader は PDG 全チーム（PMOT を含む）で許可する。
            team_leader: PDG_TEAMS_FOR_PERMISSION,
        },
    },
    /**
     * 改善要望を判断する（やる / やらない）。
     *
     * **「この要望を実行してよいか」はプロダクトの判断**なので、ほとんどの人には開けない。
     * AI が人に投げた要望を捌くのは CTO（`admin`）だけ。
     *
     * 閲覧は絞らない——自分が出した要望がなぜ止まっているかは、出した人にも見えてよい。
     * 自分の要望の**取り下げ**も別経路（出した人だけ・domain 側で判定）なのでこの権限は要らない。
     */
    DECIDE_IMPROVEMENT_REQUEST: ['admin'],
    /**
     * 改善要望をチーム課題（組織課題ボード）へ移す。移した要望は「課題に移動」で閉じる。
     *
     * CTO（`admin`）に加えて **PMO チーム（`pi_pmot`）の所属者**に開く。PMO は要望を案件と
     * 組織の課題に捌く役で、プロダクトの改修ではなく運用・体制の話だと判断したものをチームの
     * 課題へ渡す。「やる / やらない」（`DECIDE_IMPROVEMENT_REQUEST`）は開かない——移すのは
     * 置き場所を変える判断で、プロダクトとして実行してよいかの判断ではない。
     * 移動先のボードを見られるかは組織課題の domain が別に判定する。
     */
    MOVE_IMPROVEMENT_REQUEST: {
        roles: ['admin'],
        teamConditions: {
            group_manager: [PMO_TEAM],
            team_leader: [PMO_TEAM],
            member: [PMO_TEAM],
            pmo: [PMO_TEAM],
            dle: [PMO_TEAM],
            architect: [PMO_TEAM],
            tech_lead: [PMO_TEAM],
        },
    },
    /**
     * 事業 × チームの担当（DLE / DLE サブ / DLD / TechLead / チームリーダー）を設定する。
     *
     * **組織の運用マスタなので PMO と CTO に閉じる。** ここが狂うと要件定義レビューの担当と
     * バグ票の開発ツリー子票の担当が同時にずれるため、チーム内の誰でも触れる形にはしない。
     * `group_manager` を入れていないのは、担当の割り当てが自分のグループに閉じないため
     * （1 チームが複数事業に出てくる）。
     */
    MANAGE_TEAM_ASSIGNMENTS: ['admin', 'pmo'],
    /** DB辞書編集 */
    EDIT_DB_DICTIONARY: ['admin', 'architect', 'dle', 'tech_lead'],
    /** バグ評価閲覧 */
    VIEW_BUG_EVALUATION: ['admin', 'group_manager', 'team_leader', 'architect', 'tech_lead'],
    /**
     * QA責任評価閲覧（参照）。チームリーダーはQAチームに限らず全 team_leader が対象。
     * ロールは組織ポジションのみを表現し「PI Div 所属か」はこの権限では表せないため、
     * 実際の PI Div スコープ（エンジニア組織の team_leader に限定）は呼び出し側の
     * `isPiDivOrAdmin`（web: canAccessPage / api: requirePiDivOrAdmin）で別途 enforce する。
     */
    VIEW_QA_RESPONSIBILITY: [
        'admin',
        'group_manager',
        'architect',
        'tech_lead',
        'team_leader',
    ],
    /** QA責任評価の確定（admin/architect 無条件、team_leaderはQAチーム所属のみ） */
    CONFIRM_QA_RESPONSIBILITY: {
        roles: ['admin', 'architect'],
        teamConditions: {
            team_leader: ['QA team'],
        },
    },
    /** エンジニア評価閲覧（adminは全件、group_manager/team_leaderは自グループ/チームのみ） */
    VIEW_ENGINEER_EVALUATION: ['admin', 'group_manager', 'team_leader'],
    /**
     * 他人の DLE 評価の閲覧（MyPage「DLE評価」タブで対象 DLE を切り替える）。
     *
     * **`SUBMIT_DLE_EVALUATION`（全社員開放）とは別物**。あちらは「アンケートに答えられるか」で、
     * こちらは「評価された結果を本人以外が読めるか」。PL が書いた率直な評価とコメントが
     * 中身なので、回答の入口と同じ広さで開けてはいけない。
     *
     * 本人が自分の評価を読む経路（`GET /dle-evaluation/my-scores`）はこの権限を要求しない。
     * 自分のぶんしか返さないため。
     *
     * PMOT は DLE 体制そのものを運用しているチームなので、ロールに依らず所属で許可する
     * （`VIEW_PMO_BOARD` と同じ考え方）。team_leader は所属チームを問わず roles 側で許可する。
     */
    VIEW_DLE_EVALUATION: {
        roles: ['admin', 'group_manager', 'team_leader'],
        teamConditions: {
            member: [PMO_TEAM],
            pmo: [PMO_TEAM],
            dle: [PMO_TEAM],
            architect: [PMO_TEAM],
            tech_lead: [PMO_TEAM],
            external: [PMO_TEAM],
        },
    },
    /**
     * 1on1 ペアの開設・共有相手の管理・要約の確定。閲覧はロールではなくペア doc の
     * participants で判定するため、この権限は「開設できるか」だけを表す
     */
    MANAGE_ONE_ON_ONE: ['admin', 'group_manager', 'team_leader'],
    /**
     * spec（仕様グラフ）の管理者。リーダー以上（Dhik の決定 2026-09-27）。
     *
     * - spec MCP: `manage_spec_masters` のうち**承認を通らずに公開される操作**（動詞・単位の
     *   登録と、全マスターの削除。`masterWriteRequiresAdmin` が true を返すもの）だけを限る。
     *   承認を通るもの（用語・アクター・ストリーム・ステージ・ドメイン）は MCP では限らない
     * - spec コンソール: 承認・承認の一覧・確定と、担当者かの照合（`getApprovalAccess`）を限る。
     *   承認を通るものはここが関門になる
     *
     * MCP とコンソールは両方この権限を `checkPermission` で引く。ロールの並びを各入口に写さない。
     */
    MANAGE_SPEC_GRAPH: ['admin', 'group_manager', 'team_leader'],
    /**
     * 案件アサイン（全体PJ看板 / PMO看板 → 案件をアサインする）。
     *
     * 許可範囲は 3 つ: **PMOT 所属（ロールを問わない）/ PDG の team_leader / admin**。
     * アサインは PMO の意思決定そのもので、Backlog の 開発リーダー・アサイン状況・アサイン決定日と
     * 子票担当者を書き換え、案件チャンネルへ通知が飛ぶ（取り消しは人手になる）。`CREATE_PROJECT_INTAKE`
     * のような全社開放にはしない。
     *
     * PMOT をロール非依存にしているのは、PMOT の実メンバーが `pmo` ロールを持つとは限らない
     * （`member` のまま運用されている）ため。ロールで絞ると PMO 本人が自分の業務を実行できない。
     * そのため `teamConditions` の全ロールに `PMO_TEAM` を並べ、`team_leader` だけ PDG 全チームを
     * 許す（PDG の team_leader は自チームのアサインを回すため）。
     */
    ASSIGN_PROJECT: {
        roles: ['admin'],
        teamConditions: {
            // PMOT 所属はロールを問わず許可する（下の 8 行は「PMOT なら誰でも」の表現）。
            group_manager: [PMO_TEAM],
            member: [PMO_TEAM],
            architect: [PMO_TEAM],
            dle: [PMO_TEAM],
            tech_lead: [PMO_TEAM],
            pmo: [PMO_TEAM],
            external: [PMO_TEAM],
            // team_leader は PDG 全チーム（PMOT を含む）で許可する。
            team_leader: PDG_TEAMS_FOR_PERMISSION,
        },
    },
    /**
     * 案件アサイン履歴の閲覧とアサイン意図の保存。
     *
     * **リーダー以上（admin / group_manager / team_leader）と PMO に開く。** PMO は
     * `pmo` ロールに加えて PMOT（`pi_pmot`）の所属者もロールを問わず含める——PMOT は
     * `ASSIGN_PROJECT` でアサインを実行する側なので、自分のしたアサインの履歴が見られない
     * というねじれを作らない。
     */
    VIEW_ASSIGNMENT_HISTORY: {
        roles: ['admin', 'group_manager', 'team_leader', 'pmo'],
        teamConditions: {
            member: [PMO_TEAM],
            architect: [PMO_TEAM],
            dle: [PMO_TEAM],
            tech_lead: [PMO_TEAM],
            external: [PMO_TEAM],
        },
    },
};
/**
 * 権限チェック（ロール + チーム条件対応）。
 *
 * 複数ロール保持時は「保持しているロールの中に許可された役割があるか」の existential
 * チェック（`.some()`）であり、「広いロールが勝つ」という優先順位の概念はない。
 * @graph-connects none
 */
export function checkPermission(userRoles, permission, userTeamName) {
    if (!userRoles || userRoles.length === 0)
        return false;
    const rule = PORTAL_PERMISSIONS[permission];
    // 単純なロール配列
    if (Array.isArray(rule)) {
        return userRoles.some((role) => rule.includes(role));
    }
    // 条件付き権限
    const conditional = rule;
    if (userRoles.some((role) => conditional.roles.includes(role))) {
        return true;
    }
    if (!userTeamName)
        return false;
    return userRoles.some((role) => conditional.teamConditions[role]?.includes(userTeamName));
}
/**
 * 保持ロール配列から後方互換の単一 `PortalRole` を導出する（移行期間の transitional
 * ヘルパー。他アプリ共有 Firestore `productPortalRole`（単数）互換書き込み、旧クライアント
 * 向け derived レスポンス用）。
 *
 * - `admin` を保持していれば常に `admin`（横断 admin は他ロールと同時付与されても admin 優先）
 * - それ以外は `PORTAL_ROLES` の宣言順で最初に一致したロール
 * - 保持ロールが空/未設定なら undefined
 * @graph-connects none
 */
export function derivePrimaryPortalRole(portalRoles) {
    if (!portalRoles || portalRoles.length === 0)
        return undefined;
    if (portalRoles.includes('admin'))
        return 'admin';
    return PORTAL_ROLES.find((role) => portalRoles.includes(role));
}
/**
 * 「保持ロールが member 以外を含まないか」の判定。pmo によるアカウント操作・
 * impersonate 制約（対象は member ロールのみ操作可）で使う共通ヘルパー。
 *
 * `roles が空、または ['member'] のみ` を許可する existential チェックであり、
 * 「member を含むか」ではない点に注意する。「member を含むか」で判定すると
 * `['member', 'admin']` のような組み合わせが素通りしてしまう抜け穴になる。
 * @graph-connects none
 */
export function isMemberOnlyRoleSet(portalRoles) {
    if (!portalRoles || portalRoles.length === 0)
        return true;
    return portalRoles.every((role) => role === 'member');
}
//# sourceMappingURL=portal.js.map